import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const keycloakService = inject(KeycloakService);

  // Add token to all HTTP requests (only HTTP requests reach this interceptor)
  return from(addTokenToRequest(req, keycloakService)).pipe(
    switchMap(request => next(request)),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        keycloakService.clearToken();
        keycloakService.logout(window.location.origin);
        // keycloakService.login({ redirectUri: window.location.href });
      }
      return throwError(() => error);
    })
  );
};

async function addTokenToRequest<T>(
  req: HttpRequest<T>,
  keycloakService: KeycloakService
): Promise<HttpRequest<T>> {
  // Guard: user not logged in, proceed without token
  try {
    if (!keycloakService.isLoggedIn()) {
      return req;
    }
  } catch {
    // In case isLoggedIn throws or is async-resolved incorrectly, do not block request
    return req;
  }

  // Guard: token expired and refresh failed -> proceed without token
  if (keycloakService.isTokenExpired()) {
    try {
      const refresh_time = 30;
      await keycloakService.updateToken(refresh_time);
      // If not refreshed, we still proceed (token was valid or near expiry but unchanged)
    } catch {
      return req;
    }
  }

  // Acquire token and attach if present; otherwise return original request
  try {
    const token = await keycloakService.getToken();
    if (!token) {
      return req;
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    // Preserve existing Content-Type; only set if missing
    const hasContentType = !!req.headers.get('Content-Type');
    if (!hasContentType) {
      headers['Content-Type'] = 'application/json';
    }

    return req.clone({ setHeaders: headers });
  } catch {
    return req;
  }
}